import angular from "angular";
import { Arrays, Translations } from "@salaxy/core";
import {
  UiHelpers,
  UiTranslatorService,
  UiTranslatorDictionaryItem
} from "../../services";

/**
 * Tool that allows translate user interfaces.
 */
export class UiTranslatorController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["UiHelpers", "$timeout", "UiTranslatorService"];

  /** User interface mode for the component. */
  public mode: "table" | "fieldset";

  /** Array of dictionary items shown in the component (the original unedited values). */
  public allKeyValues: Array<UiTranslatorDictionaryItem> = [];

  /** Local variable used to store texts that have been edited. */
  public serviceModelTexts: Array<{ fullKey: string, fi?: string, en?: string, sv?: string }> = [];

  /** Contains boolean values to indicate whether columns should be shown or not. */
  public showCols: UiTranslatorDictionaryItem = {
    fullKey: "true",
    fi: "true",
    en: "true",
    sv: "true",
  };

  /** AngularJS filter for the translator table: rows filtering.  */
  public listFilter: UiTranslatorDictionaryItem = {};

  /** Show spinner if the data is loading. */
  public isLoading = false;

  /**
   * Texts from serviceModel that are being edited
   * This is in format { [key: string]: string } where key
   * is a full key starting from the language, e.g. "fi.SALAXY.NG1_Sitemap.home"
   * and value is always an indidivual text (never an object).
   */
  public texts: { [key: string]: string };

  private _filter: string | string[];

  /**
   * Creates a new UiTranslatorController
   * @ignore
   */
  constructor(private uiHelpers: UiHelpers, private $timeout: angular.ITimeoutService, private uiTranslatorService: UiTranslatorService) {
    // For dependency injection.
  }

  /** Init */
  public $onInit = () => {
    this.initTranslationTable();
    this.initTexts();
  };

  /**
   * Beginning of the full key that is used to filtering the dictionary items (allKeyValues filter).
   * The value may be a string, array or comma separated string of values.
   */
  public get filter(): string | string[] {
    return this._filter;
  }
  public set filter(value: string | string[]) {
    this._filter = value;
    this.updateAllKeyValues();
  }

  /** Updates this.text and keeps reference to parent */
  public updateTexts() {
    angular.copy(this.toTexts(), this.texts);
  }

  /**
   * Check if key exists in serviceModelTexts for specified language.
   * @param fullkey Dictionary key
   * @param lang Language that is checked.
   * @returns True if the provided key exists
   */
  public checkIfExistsInTexts(fullkey: string, lang: string): boolean {
    return !!this.serviceModelTexts.find(x => x.fullKey === fullkey && lang in x);
  }

  /**
   * Check if item can be deleted from serviceModelTexts
   * @param fullKey dictionary key
   * @returns True if the item can be deleted.
   */
  public canDeleteFromText(fullKey: string): boolean {
    return !!this.serviceModelTexts.find(x => x.fullKey === fullKey);
  }

  /**
   * Finds and return item from serviceModelTexts
   * @param fullkey search value
   * @returns serviceModelTexts item
   */
  public getEditableText(fullkey: string): { fullKey: string, fi?: string, en?: string, sv?: string } {
    return this.serviceModelTexts.find(({ fullKey }) => fullKey === fullkey);
  }

  /**
   * Adds item to serviceModelTexts
   * @param fullKey fullKey to be added
   */
  public addToTexts(fullKey: string, lang: string): void {
    const index = this.serviceModelTexts.findIndex(x => x.fullKey === fullKey);
    if (index > -1) {
      this.serviceModelTexts[index][lang] = "";
    } else {
      const obj: any = { fullKey: fullKey };
      obj[lang] = "";
      this.serviceModelTexts.push(obj);
    }
    this.updateTexts();
  }

  /**
   * Remove item from serviceModelTexts
   * @param fullKey fullKey search value
   */
  public removeFromTexts(fullKey: string): void {
    const index = this.serviceModelTexts.findIndex(x => x.fullKey === fullKey);
    this.serviceModelTexts.splice(index, 1);
    this.updateTexts();
  }

  /**
   * Save current diff to JSON file
   */
  public downloadTexts(): void {
    const diff = this.getDiffDictionary();
    const str = JSON.stringify(diff);
    const bytes = new TextEncoder().encode(str);
    const blob = new Blob([bytes], {
      type: "application/json;charset=utf-8"
    });
    this.uiHelpers.downloadFile(blob, "diff");
  }

  private initTranslationTable() {
    this.isLoading = true;
    this.$timeout().then(() => {
      if (this.uiTranslatorService.getDictionary().length < 1) {
        Translations.instance.getBaseDictionary().then(baseDictionary => {
          this.createKeyValues(baseDictionary, "");
          this.updateAllKeyValues();
          this.$timeout(); // getBaseDictionary uses fetch() => Need to call timeout to trigger the UI refresh.
          this.isLoading = false;
        });
      } else {
        this.updateAllKeyValues();
        this.isLoading = false;
      }
    });
  }

  private initTexts() {
    this.serviceModelTexts = this.fromTexts(this.texts);
  }

  private fromTexts(texts: any) {
    texts = texts || {};
    const arr: Array<{ fullKey: string, fi?: string, en?: string, sv?: string }> = [];
    Object.keys(texts).forEach(key => {
      const lang = key.substr(0, 2);
      const fullKey = key.substring(3);
      // First iteration, arr is empty
      if (!arr.length) {
        const serviceModelTranslation: { fullKey: string, fi?: string, en?: string, sv?: string } = { fullKey: fullKey };
        serviceModelTranslation[lang] = texts[key];
        arr.push(serviceModelTranslation);
      } else {
        // Find index of fullKey
        const index = arr.findIndex(item => item.fullKey === fullKey);
        if (index > -1) {
          // index exists, add texts[key] to object
          arr[index][lang] = texts[key];
        } else {
          // index not found, add new object to array
          const serviceModelTranslation: { fullKey: string, fi?: string, en?: string, sv?: string } = { fullKey: fullKey };
          serviceModelTranslation[lang] = texts[key];
          arr.push(serviceModelTranslation);
        }
      }
    });
    return arr;
  }

  private toTexts() {
    const obj: { [key: string]: string } = {};
    this.serviceModelTexts.forEach(text => {
      Object.keys(text).forEach(key => {
        if (key !== "fullKey") {
          const newFullKey = `${key}.${text.fullKey}`;
          obj[newFullKey] = text[key];
        }
      });
    });
    return obj;
  }

  private updateAllKeyValues() {
    this.allKeyValues = this.uiTranslatorService.getDictionary();
    const filter = Arrays.assureArray(this.filter);
    if (filter.length) {
      this.allKeyValues = this.allKeyValues.filter(x => filter.find((f) => x.fullKey.startsWith(f)));
    }
  }

  private createKeyValues(dictionary: any, path: string) {
    Object.keys(dictionary).forEach(key => {
      if (typeof dictionary[key] === "object") {
        this.createKeyValues(dictionary[key], `${path}.${key}`);
      } else {
        const fullPath = `${path}.${key}`;
        const fullKey = fullPath.substring(4);
        const lang = fullPath.slice(1, 3);
        const allKeyValues = this.uiTranslatorService.getDictionary();
        const index = allKeyValues.findIndex(item => item.fullKey === fullKey);
        if (index > -1) {
          allKeyValues[index][lang] = dictionary[key];
        } else {
          const splitKeys = fullKey.split(".");
          const obj: UiTranslatorDictionaryItem = {
            fullKey,
            shortKey: splitKeys[splitKeys.length - 1],
            fileName: splitKeys[1],
            keyComponent: splitKeys[2],
            keyValue: splitKeys[3]
          };
          obj[lang] = dictionary[key];
          this.uiTranslatorService.addToDictionary(obj);
        }
      }
    });
  }
  private getDiffDictionary() {
    const diffBase = {
      fi: {},
      en: {},
      sv: {}
    };
    this.serviceModelTexts.forEach(item => {
      const subKeys = item.fullKey.split(".");
      Object.keys(diffBase).forEach(lang => {
        let currentNode = diffBase[lang];
        for (let i = 0; i < subKeys.length; i++) {
          if (i === subKeys.length - 1) {
            currentNode = currentNode[subKeys[i]] = item[lang];
          } else {
            // Check if subKeys[i] key exists in currentNode
            if (!(subKeys[i] in currentNode)) {
              // Not found, create new key with subKeys[i], assign it to {} and set to currentNode
              currentNode = currentNode[subKeys[i]] = {};
            } else {
              // Found, assign currentNode to currentNode[subKeys[i]]
              currentNode = currentNode[subKeys[i]];
            }
          }
        }
      });
    });
    return diffBase;
  }
}

